export type FeatureImage = {
  id: string;
  title: string;
  caption: string;
  alt: string;
  category: 'business' | 'website' | 'ai' | 'feature';
  src: string;
  width?: number;
  height?: number;
};

export type WorkspaceCategory = 'all' | 'website' | 'ai' | 'business';

export const FEATURE_IMAGE_BASE = '/features';

export const GALLERY_IMAGES: FeatureImage[] = [
  {
    id: 'manage-services',
    title: 'Services management',
    caption: 'Service cards, categories, prices, and durations with the full owner sidebar.',
    alt: 'AA Nails services management screenshot retaining the complete left sidebar.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/manage-services.png`,
    width: 1533,
    height: 1077,
  },
  {
    id: 'roles',
    title: 'Roles and Access',
    caption: 'Owner, front-desk, and professional permissions with the full owner sidebar.',
    alt: 'AA Nails roles and access screenshot retaining the complete left sidebar.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/roles.png`,
    width: 1533,
    height: 1077,
  },
  {
    id: 'personnel',
    title: 'Personnel',
    caption: 'Team members and service permissions. Names, initials, and email addresses are masked.',
    alt: 'AA Nails personnel screenshot retaining the complete left sidebar.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/personnel.png`,
    width: 1533,
    height: 1077,
  },
  {
    id: 'closures',
    title: 'Closures',
    caption: 'The closure-management screen with the full owner sidebar. No upcoming closures are configured in this capture.',
    alt: 'AA Nails closures screenshot retaining the complete left sidebar.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/closures.png`,
    width: 1533,
    height: 1077,
  },
  {
    id: 'testimonials',
    title: 'Testimonials',
    caption: 'The testimonial-management screen with the full owner sidebar. No testimonials are configured in this capture.',
    alt: 'AA Nails testimonials screenshot retaining the complete left sidebar.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/testimonials.png`,
    width: 1533,
    height: 1077,
  },
  {
    id: 'owner-navigation',
    title: 'Every owner menu group',
    caption: 'Five genuine sidebar captures, shown together. The app opens one menu group at a time.',
    alt: 'Composite showing Business, Team, Site, My Work, and AACore Platform navigation expanded in five separate captures.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/navigation-overview.png`,
    width: 1395,
    height: 1117,
  },
  {
    id: 'calendar',
    title: 'See your appointment calendar',
    caption: 'Appointments and professional assignments in one view. Customer identifiers are hidden.',
    alt: 'Actual AACore appointment calendar, with customer and staff identifiers obscured.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/calendar.png`,
    width: 1100,
    height: 757,
  },
  {
    id: 'branding',
    title: 'Make the website your own',
    caption: 'Choose branding and a preset color theme for your public website.',
    alt: 'Actual AACore business branding controls.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/branding.png`,
    width: 512,
    height: 467,
  },
  {
    id: 'hours',
    title: 'Set business hours',
    caption: 'Your timezone and operating hours support the booking schedule.',
    alt: 'Actual business timezone and operating-hour settings.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/hours.png`,
    width: 1050,
    height: 369,
  },
  {
    id: 'booking-modes',
    title: 'Choose where services happen',
    caption: 'Offer appointments in-store, on-site, or both.',
    alt: 'Actual service location settings with in-store, on-site, and both options.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/booking-modes.png`,
    width: 1050,
    height: 300,
  },
  {
    id: 'policies',
    title: 'Make booking expectations clear',
    caption: 'Review cancellation, rescheduling, and deposit settings.',
    alt: 'Actual cancellation, rescheduling, and deposit configuration.',
    category: 'business',
    src: `${FEATURE_IMAGE_BASE}/policies.png`,
    width: 1050,
    height: 330,
  },
  {
    id: 'website',
    title: 'Your public business website',
    caption: 'Contact details, hours, photos, and a booking entry point.',
    alt: 'AA Nails demo website with public business information and booking controls.',
    category: 'website',
    src: `${FEATURE_IMAGE_BASE}/website-desktop.png`,
    width: 1419,
    height: 872,
  },
  {
    id: 'services',
    title: 'Services customers can browse',
    caption: 'Categories, prices, and durations in one place.',
    alt: 'AA Nails public catalogue with service categories, prices, and durations.',
    category: 'website',
    src: `${FEATURE_IMAGE_BASE}/services-desktop.png`,
    width: 1419,
    height: 872,
  },
  {
    id: 'website-pair',
    title: 'Your website and services',
    caption: 'Two actual screens, paired to show the customer experience.',
    alt: 'Side-by-side screenshots of the demo business website and its service catalogue.',
    category: 'website',
    src: `${FEATURE_IMAGE_BASE}/website-and-services.png`,
    width: 2576,
    height: 847,
  },
  {
    id: 'mobile-pair',
    title: 'Your business on mobile',
    caption: 'The business page and service list on a smaller screen.',
    alt: 'Paired mobile screenshots of the AA Nails public website and service catalogue.',
    category: 'website',
    src: `${FEATURE_IMAGE_BASE}/mobile-website-services.png`,
    width: 812,
    height: 660,
  },
  {
    id: 'ai-welcome',
    title: 'Start a conversation',
    caption: 'Ask about services, hours, or making an appointment.',
    alt: 'Fresh Ask AACore AI greeting on the AA Nails demo website.',
    category: 'ai',
    src: `${FEATURE_IMAGE_BASE}/ai-welcome-fresh.png`,
    width: 364,
    height: 568,
  },
  {
    id: 'ai-choose',
    title: 'Choose services and a date',
    caption: 'The AI guides the customer through booking choices.',
    alt: 'AI conversation choosing services, a professional, and a date.',
    category: 'ai',
    src: `${FEATURE_IMAGE_BASE}/ai-step-2.png`,
    width: 460,
    height: 720,
  },
  {
    id: 'ai-time',
    title: 'Pick an available time',
    caption: 'Choose from the available appointment slots.',
    alt: 'Ask AACore AI presents available appointment times.',
    category: 'ai',
    src: `${FEATURE_IMAGE_BASE}/ai-step-3.png`,
    width: 460,
    height: 720,
  },
  {
    id: 'ai-review',
    title: 'Review before confirming',
    caption: 'The customer checks the details and confirms the booking.',
    alt: 'AI appointment summary shows services, professional, date, time, and total.',
    category: 'ai',
    src: `${FEATURE_IMAGE_BASE}/ai-step-4.png`,
    width: 460,
    height: 720,
  },
  {
    id: 'ai-booked',
    title: 'The appointment is booked',
    caption: 'A confirmation with a link to the appointment.',
    alt: 'Actual demo booking confirmation in Ask AACore AI.',
    category: 'ai',
    src: `${FEATURE_IMAGE_BASE}/ai-step-5.png`,
    width: 460,
    height: 720,
  },
  {
    id: 'ai-pair',
    title: 'From chat to booking review',
    caption: 'Related AI screens shown together.',
    alt: 'Two real AI screenshots showing service choices and booking review.',
    category: 'ai',
    src: `${FEATURE_IMAGE_BASE}/ai-booking-review.png`,
    width: 992,
    height: 806,
  },
  {
    id: 'aahairs-mobile-pair',
    title: 'AA Hairs, on mobile',
    caption: 'Photo gallery and services, side by side.',
    alt: 'Two equal-height mobile screenshots of AA Hairs, showing its photo gallery and the service list.',
    category: 'website',
    src: `${FEATURE_IMAGE_BASE}/aahairs-gallery-services-pair.png`,
    width: 752,
    height: 568,
  },
];

export const FEATURE_IMAGES: FeatureImage[] = [
  {
    id: 'aahairs-gallery',
    title: 'AA Hairs photo gallery',
    caption: 'Showcase your work on your business website.',
    alt: 'AA Hairs mobile photo gallery and hairstyle thumbnails.',
    category: 'feature',
    src: `${FEATURE_IMAGE_BASE}/aahairs-mobile-gallery.png`,
    width: 368,
    height: 568,
  },
  {
    id: 'aahairs-services',
    title: 'AA Hairs services',
    caption: 'Customers browse services, prices, and durations.',
    alt: 'AA Hairs mobile service list with prices and durations.',
    category: 'feature',
    src: `${FEATURE_IMAGE_BASE}/aahairs-mobile-services.png`,
    width: 368,
    height: 568,
  },
];

export const ALL_FEATURE_IMAGES = [...GALLERY_IMAGES, ...FEATURE_IMAGES];

export const WORKSPACE_GROUPS: Record<WorkspaceCategory, { label: string; images: string[] }> = {
  all: {
    label: 'All screens',
    images: GALLERY_IMAGES.map((image) => image.id),
  },
  website: {
    label: 'Business website',
    images: ['aahairs-mobile-pair', 'website', 'services', 'website-pair', 'mobile-pair'],
  },
  ai: {
    label: 'AI booking',
    images: ['ai-welcome', 'ai-choose', 'ai-time', 'ai-review', 'ai-booked', 'ai-pair'],
  },
  business: {
    label: 'Business tools',
    images: [
      'manage-services',
      'roles',
      'personnel',
      'closures',
      'testimonials',
      'owner-navigation',
      'calendar',
      'branding',
      'hours',
      'booking-modes',
      'policies',
    ],
  },
};

export function findFeatureImage(id: string): FeatureImage | undefined {
  return ALL_FEATURE_IMAGES.find((image) => image.id === id);
}

export function getWorkspaceImages(category: WorkspaceCategory): FeatureImage[] {
  return WORKSPACE_GROUPS[category].images
    .map((id) => GALLERY_IMAGES.find((image) => image.id === id))
    .filter((image): image is FeatureImage => Boolean(image));
}
